/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { faSyncAlt } from '@fortawesome/free-solid-svg-icons/faSyncAlt'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ListCard from '../../../client/js/components/ListCard'
import DeleteSelectionModal from '../../../client/js/components/modals/DeleteSelectionModal'
import PageTitle from '../../../client/js/components/PageTitle'
import SearchForm from '../../../client/js/components/search/SearchForm'
import SelectionAlert from '../../../client/js/components/selection/SelectionAlert'
import useSelection from '../../../client/js/components/selection/useSelection'
import useQueryParams from '../../../client/js/hooks/useQueryParams'
import RequirePerm from '../../auth/components/RequirePerm'
import UserList from './UserList'
import { deleteUser, fetchUsers } from '../lib/userAPIClient'
import usePaginationQuery from '../../../client/js/components/pagination/usePaginationQuery'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import LoadingHelper from '../../../client/js/components/LoadingHelper'
import { getUserCreateLink } from '../lib/userLinks'
import { Button, Container } from '@mantine/core'
import AnchorLink from '../../../client/js/components/AnchorLink'
import Toolbar from '../../../client/js/components/Toolbar'
import Page from '../../../client/js/components/Page'

export type UserListQuery = {
  limit?: string,
  page?: string,
  search?: string
}

const initialQuery: UserListQuery = {
  limit: '10',
  page: '1'
}

function UserListPage () {
  const intl = useIntl()

  const title = intl.formatMessage({
    id: 'users',
    defaultMessage: 'Utilisateurs'
  })

  const [searchParams, setSearchParams] = useQueryParams()
  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const queryParams = useMemo(() => {
    return {
      ...initialQuery,
      ...searchParams,
      page,
      limit
    }
  }, [limit, page, searchParams])

  // Items loading.
  const usersQuery = useQuery({
    enabled: true,
    queryKey: ['users', queryParams],
    queryFn: () => fetchUsers(queryParams).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })
  const items = useMemo(() => (
    (usersQuery.data != null ? getHalEmbedded(usersQuery.data)?.users : null) || []
  ), [usersQuery.data])

  const setFilter = useCallback((name: string, value: string) => {
    setSearchParams((s) => ({ ...s, page: '1', [name]: value }))
  }, [setSearchParams])

  const setSearch = useCallback((search?: string) => {
    setFilter('search', search)
  }, [setFilter])

  // Items selection.
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const selection = useSelection<number>({
    multiple: true,
    items: selectedItems,
    onChange: setSelectedItems,
    source: items.map((el) => el.id)
  })

  // Items deletion.
  const handleDelete = useCallback((id: number) => deleteUser(id), [])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((s) => !s)
  }, [])

  return (
    <Page>
      <Container size="xl">
        <PageTitle
          hidden
          title={title}
        />

        <ListCard
          pagination={pagination}
          selection={selection}
          selectionActions={<SelectionAlert>
            <RequirePerm
              permissions={['write:user']}
              render={() => (
                <Button onClick={toggleDeleteModal}>
                  <FontAwesomeIcon
                    fixedWidth
                    icon={faTrash}
                  />
                </Button>
              )}
            />
          </SelectionAlert>}
          toolbar={<Toolbar>
            <RequirePerm
              permissions={['write:role']}
              render={() => (
                <Button
                  variant="filled"
                  component={AnchorLink}
                  to={getUserCreateLink()}
                >
                  <FormattedMessage
                    id="create_user"
                    defaultMessage="Créer un utilisateur"
                  />
                </Button>
              )}
            />
            <SearchForm
              initialValue={queryParams.search}
              onSubmit={setSearch}
              submitting={usersQuery.isFetching}
            />
            <Button
              loading={usersQuery.isFetching}
              onClick={() => {
                usersQuery.refetch()
              }}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faSyncAlt}
              />
            </Button>
          </Toolbar>}
        >
          <LoadingHelper
            query={usersQuery}
            render={() => (
              <UserList items={items} />
            )}
          />
        </ListCard>
      </Container>

      {isDeleteModalOpen
        ? <DeleteSelectionModal
          opened={isDeleteModalOpen}
          items={selection.items}
          onConfirm={handleDelete}
          onSuccess={() => {
            selection.clear()
            usersQuery.refetch()
          }}
          onClose={toggleDeleteModal}
        />
        : null}
    </Page>
  )
}

export default UserListPage
